import React, { useEffect, useRef, useState } from 'react';
import { Plus, X } from 'lucide-react';
import Button from '../common/Button.js';
import ArticleTypeSelect from './ArticleTypeSelect.js';
import {
  emptyBusArticle,
  sumBusArticleFreight,
  sumBusArticleQty,
  sumBusArticleWeight,
  toNum,
  cloneBusArticleConfig,
  MAX_PACKAGE_CLONE_COUNT,
} from '../../utils/cargoParcelUtils.js';

const compactInputClass =
  'w-full min-w-0 px-2 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs sm:text-sm disabled:bg-gray-50 disabled:text-gray-700';

const Field = ({ label, children, className = '', required = false }) => (
  <div className={`min-w-0 ${className}`}>
    <label className="block text-[11px] sm:text-xs font-medium text-gray-500 mb-1">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

/**
 * Multi-article form for bus shipments — same tab + clone layout as cargo PackageDimensionForm.
 */
const BusArticlePackagesForm = ({
  articles = [],
  onChange,
  readOnly = false,
  freightByWeight = false,
  startSrNo = 1,
  headerHint = '',
  title = '',
  badge = '',
}) => {
  const rows = articles.length ? articles : [emptyBusArticle(startSrNo)];
  const [activeIndex, setActiveIndex] = useState(0);
  const [multiplyCount, setMultiplyCount] = useState('');
  const [multiplyError, setMultiplyError] = useState('');
  const qtyInputRef = useRef(null);

  useEffect(() => {
    if (activeIndex > rows.length - 1) {
      setActiveIndex(Math.max(0, rows.length - 1));
    }
  }, [rows.length, activeIndex]);

  const safeIndex = Math.min(activeIndex, rows.length - 1);
  const row = rows[safeIndex] || emptyBusArticle(startSrNo);
  const canRemove = !readOnly && rows.length > 1;

  const updateRow = (index, patch) => {
    if (readOnly) return;
    onChange(rows.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  const addRow = () => {
    if (readOnly) return;
    const nextSr = startSrNo + rows.length;
    onChange([...rows, emptyBusArticle(nextSr)]);
    setActiveIndex(rows.length);
  };

  const cloneActiveRows = () => {
    if (readOnly) return;
    const count = Number.parseInt(String(multiplyCount).trim(), 10);
    if (!Number.isFinite(count) || count < 1) {
      setMultiplyError('Enter a valid count (1 or more)');
      return;
    }
    if (count > MAX_PACKAGE_CLONE_COUNT) {
      setMultiplyError(`Maximum ${MAX_PACKAGE_CLONE_COUNT} articles at once`);
      return;
    }
    if (rows.length + count > MAX_PACKAGE_CLONE_COUNT) {
      setMultiplyError(`Total articles cannot exceed ${MAX_PACKAGE_CLONE_COUNT}`);
      return;
    }
    setMultiplyError('');
    const source = rows[safeIndex] || rows[0];
    const clones = Array.from({ length: count }, (_, i) =>
      cloneBusArticleConfig(source, startSrNo + rows.length + i),
    );
    onChange([...rows, ...clones]);
    setActiveIndex(rows.length);
    setMultiplyCount('');
  };

  const removeRow = (index) => {
    if (!canRemove) return;
    const next = rows
      .filter((_, i) => i !== index)
      .map((item, i) => ({
        ...item,
        sr_no: startSrNo + i,
        package_name: item.package_name || item.article_type || 'PACKAGE',
      }));
    onChange(next);
    setActiveIndex((prev) => {
      if (prev > index) return prev - 1;
      if (prev >= next.length) return Math.max(0, next.length - 1);
      return prev;
    });
  };

  const tabLabel = (index) => {
    const item = rows[index] || {};
    const qty = item.qty || '0';
    if (freightByWeight) {
      const wt = toNum(item.actual_weight).toFixed(0);
      return { title: `Article ${startSrNo + index}`, meta: `${qty} qty · ${wt} kg` };
    }
    const line = (toNum(item.article_amount) * Math.max(toNum(item.qty, 1), 1)).toFixed(0);
    return { title: `Article ${startSrNo + index}`, meta: `${qty} qty · ₹${line}` };
  };

  const totalQty = sumBusArticleQty(rows);
  const totalFreight = sumBusArticleFreight(rows);
  const totalWeight = sumBusArticleWeight(rows);

  return (
    <div className="space-y-3 min-w-0 mb-3">
      <div className="flex items-center gap-2 min-w-0">
        <div className="flex items-center gap-2 shrink-0">
          {title ? (
            <h3 className="text-sm sm:text-base font-semibold text-secondary whitespace-nowrap">{title}</h3>
          ) : null}
          {badge ? (
            <span className="text-[10px] sm:text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full whitespace-nowrap">
              {badge}
            </span>
          ) : null}
        </div>

        <div className="flex-1 min-w-0 overflow-x-auto">
          <div className="flex items-center gap-1.5 min-w-0">
            {rows.map((_, index) => {
              const { title: tabTitle, meta } = tabLabel(index);
              const isActive = index === safeIndex;
              return (
                <div
                  key={index}
                  className={`shrink-0 inline-flex items-center gap-0.5 rounded-lg border pl-2 pr-0.5 py-1 transition ${
                    isActive
                      ? 'bg-blue-600 border-blue-600 text-white shadow-sm'
                      : 'bg-white border-gray-200 text-gray-600'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setActiveIndex(index)}
                    className="min-w-0 text-left"
                    title={`${tabTitle} · ${meta}`}
                  >
                    <span className="block text-[11px] sm:text-xs font-semibold leading-tight whitespace-nowrap">
                      {tabTitle}
                    </span>
                  </button>
                  {canRemove && (
                    <button
                      type="button"
                      onClick={() => removeRow(index)}
                      className={`p-1 rounded-md ${
                        isActive
                          ? 'text-white/90 hover:bg-blue-500'
                          : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                      }`}
                      title={`Remove ${tabTitle}`}
                      aria-label={`Remove ${tabTitle}`}
                    >
                      <X size={12} strokeWidth={2.5} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {!readOnly && (
          <div className="flex items-center gap-1.5 shrink-0">
            <div
              className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-1.5 py-0.5"
              title="Clone active article N times"
            >
              <span className="text-[10px] sm:text-xs font-medium text-gray-500 whitespace-nowrap">×</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={multiplyCount}
                onChange={(e) => {
                  setMultiplyCount(e.target.value.replace(/\D/g, ''));
                  setMultiplyError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    cloneActiveRows();
                  }
                }}
                placeholder="N"
                className="w-10 sm:w-12 min-w-0 px-1 py-1 text-xs sm:text-sm text-center border-0 focus:outline-none focus:ring-0 bg-transparent"
                aria-label="Number of articles to clone"
              />
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={cloneActiveRows}
                title="Clone active article"
                className="!px-2 !py-1 !text-[11px] sm:!text-xs whitespace-nowrap"
              >
                Clone
              </Button>
            </div>
            <Button
              type="button"
              variant="primary"
              size="sm"
              icon={Plus}
              onClick={addRow}
              title="Add article"
              className="shrink-0"
            >
              Add
            </Button>
          </div>
        )}
      </div>

      {headerHint ? (
        <p className="text-xs sm:text-sm text-gray-500 leading-snug mb-0">{headerHint}</p>
      ) : null}

      {multiplyError ? (
        <p className="text-xs text-red-600 font-medium">{multiplyError}</p>
      ) : null}

      <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-3 sm:p-4 min-w-0 relative">
        <div className="flex items-start justify-between gap-2 mb-3">
          <p className="text-xs sm:text-sm font-semibold text-secondary">
            Article #{startSrNo + safeIndex}
          </p>
          {canRemove && (
            <button
              type="button"
              onClick={() => removeRow(safeIndex)}
              className="p-1 -mt-1 -mr-1 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50"
              title="Remove this article"
              aria-label="Remove this article"
            >
              <X size={16} strokeWidth={2.5} />
            </button>
          )}
        </div>

        <div className="grid grid-cols-3 gap-2 sm:gap-3 min-w-0 items-end">
          <Field label="Article Type" required className="min-w-0">
            <ArticleTypeSelect
              value={row.article_type || 'PACKAGE'}
              readOnly={readOnly}
              onEnterNext={() => qtyInputRef.current?.focus()}
              onChange={(next) =>
                updateRow(safeIndex, {
                  article_type: next,
                  package_name: next,
                })
              }
            />
          </Field>

          <Field label="Qty" required className="min-w-0">
            <input
              ref={qtyInputRef}
              type="number"
              min="1"
              disabled={readOnly}
              value={row.qty}
              onChange={(e) => updateRow(safeIndex, { qty: e.target.value })}
              className={compactInputClass}
            />
          </Field>

          {freightByWeight ? (
            <Field label="Weight (kg)" required className="min-w-0">
              <input
                type="number"
                min="0"
                step="0.01"
                disabled={readOnly}
                value={row.actual_weight}
                onChange={(e) =>
                  updateRow(safeIndex, {
                    actual_weight: e.target.value,
                    chargeable_weight: e.target.value,
                    article_amount: '',
                  })
                }
                className={compactInputClass}
              />
            </Field>
          ) : (
            <Field label="Article Rate (₹)" className="min-w-0">
              <input
                type="number"
                min="0"
                step="0.01"
                disabled={readOnly}
                value={row.article_amount}
                onChange={(e) => updateRow(safeIndex, { article_amount: e.target.value })}
                className={compactInputClass}
              />
            </Field>
          )}
        </div>

        <p className="mt-2 text-[11px] sm:text-xs text-gray-500">
          {freightByWeight
            ? `Row weight: ${toNum(row.actual_weight).toFixed(2)} kg`
            : `Line freight: ₹${(toNum(row.article_amount) * Math.max(toNum(row.qty, 1), 1)).toFixed(2)}`}
        </p>
      </div>

      <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
        Total qty: <span className="font-semibold text-secondary">{totalQty}</span>
        {freightByWeight ? (
          <>
            <span className="mx-2 text-gray-300">|</span>
            Total weight:{' '}
            <span className="font-semibold text-secondary">{totalWeight.toFixed(2)} kg</span>
          </>
        ) : (
          <>
            <span className="mx-2 text-gray-300">|</span>
            Article freight:{' '}
            <span className="font-semibold text-secondary">₹{totalFreight.toFixed(2)}</span>
          </>
        )}
      </p>
    </div>
  );
};

export default BusArticlePackagesForm;
